import { lazy, Suspense, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router-dom';

import ServerEmail from '~/apps/ServerEmail';
import LoadingScene from '~/components/MFEProvider/lib/ReactSuspense/LoadingScene';
import Hosting from '~/scenes/Hosting';
import Overview from '~/scenes/Hosting/scenes/HostingDetail/scenes/Overview';

import { useAlert } from './components/Alerts/alertsStore';
import { getIsDevMode } from './components/DevMode';
import { useMFEContext } from './components/MFEProvider';
import { useHttpErrorContext } from './components/MFEProvider/lib/HttpClient';
import { useBrandInfo } from './hooks/useBrandInfo';
import useFeature from './hooks/useFeature';
import useQueryFeature from './hooks/useQueryFeature';
import HostingList from './scenes/HostingList';

const Resource = lazy(() => import('~/apps/ResourceUsage'));
const SslManager = lazy(() => import('~/apps/SSLManager'));
const Redirects = lazy(() => import('~/apps/Redirects'));
const Backups = lazy(() => import('~/apps/Backups'));
const Databases = lazy(() => import('~/apps/Databases'));
const Sites = lazy(() => import('~/scenes/Sites'));
const SSHKeyManager = lazy(() => import('~/apps/SSHKeyManager'));
const ProServices = lazy(() => import('~/apps/ProServices'));
const CronJobs = lazy(() => import('~/apps/CronJobs'));
const FtpAccessManger = lazy(() => import('~/apps/FTPAccess'));
const Indexes = lazy(() => import('~/apps/Indexes'));
const Emails = lazy(() => import('~/apps/Emails'));
const Security = lazy(
  () => import('~/scenes/Hosting/scenes/HostingDetail/scenes/Security'),
);
const Resources = lazy(
  () =>
    import(
      './scenes/Hosting/scenes/HostingDetail/scenes/Overview/Cloud/scenes/Resources'
    ),
);
const ChooseDomain = lazy(
  () =>
    import(
      './scenes/Hosting/scenes/HostingDetail/components/AdvancedHosting/components/Server/ChooseDomain'
    ),
);

const PhpInfo = lazy(() => import('~/apps/Phpinfo'));
const Logs = lazy(() => import('~/apps/Logs'));
const DirectoryPrivacy = lazy(() => import('~/apps/DirectoryPrivacy'));

// This is to ensure backawards compatibility with the old hosting routes '/' that don't include the hostingId
// TODO: Once we update all the hosting Routes are using the hostingId from the route params '/:hostingId',
// we can remove '/' from the rootPaths array
const rootPaths = ['/', '/:hostingId'];

const MainRoutes = () => {
  const { t } = useTranslation('tenant');
  const generateAlert = useAlert();
  const [isAdvancedHosting] = useFeature('advanced_hosting');
  const [isDestiny] = useFeature('destiny');
  const [enableHostingTabs] = useQueryFeature('hostingtabs');
  // @ts-expect-error
  const { hostingId } = useMFEContext();

  const { error } = useHttpErrorContext();
  const { phoneNumberFormatted } = useBrandInfo();

  useEffect(() => {
    if (error) {
      generateAlert({
        severity: 'error',
        description: (
          <Trans
            t={t}
            i18nKey="timeout.description"
            values={{ phoneNumber: phoneNumberFormatted }}
          />
        ),
        showCloseBtn: true,
        persist: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const hostingRoutes = (
    <>
      <Route element={<Hosting />}>
        <Route index element={<Overview />} />
        <Route path="ssl-manager/*" element={<SslManager />} />
        <Route path="cron-jobs/*" element={<CronJobs />} />
        <Route path="ssh-keys/*" element={<SSHKeyManager />} />
        <Route path="pro-services/*" element={<ProServices />} />
        <Route path="ftp-access/*" element={<FtpAccessManger />} />
        <Route path="databases/*" element={<Databases />} />
        <Route path="redirects/*" element={<Redirects />} />
        <Route path="email/*" element={<ServerEmail />} />
        <Route path="resources/*" element={<Resources />} />
        <Route path="backups/*" element={<Backups />} />
        <Route path="phpinfo/:siteID/*" element={<PhpInfo />} />
        {(isDestiny || enableHostingTabs) && (
          <Route path="security/*" element={<Security />} />
        )}
        <Route path="logs/*" element={<Logs />} />
        <Route path="directory-privacy/*" element={<DirectoryPrivacy />} />
        <Route path="indexes/*" element={<Indexes />} />
        <Route path="resource-usage/*" element={<Resource />} />
        <Route path="mail/*" element={<Navigate to={`/${hostingId}/mail`} />} />
        <Route path=":hostingId/mail/*" element={<Emails />} />
      </Route>
      <Route path="sites/*" element={<Sites />} />
    </>
  );

  return (
    <Suspense fallback={<LoadingScene />}>
      <Routes>
        {rootPaths.map((path) => (
          <Route key={path} path={path}>
            {hostingRoutes}
          </Route>
        ))}

        {getIsDevMode(['development', 'beta']) && (
          <Route path="/hosting/*">
            <Route index element={<HostingList />} />
            <Route path=":hostingId" element={<Hosting />}>
              <Route index element={<Overview />} />
              {/* <Route path="mail/*" element={<Emails />} /> */}
            </Route>
          </Route>
        )}

        {isAdvancedHosting && (
          <Route path="setup-server/*" element={<ChooseDomain />} />
        )}

        {/* No validation needed */}
        <Route path="*" element={<LoadingScene />} />
      </Routes>
    </Suspense>
  );
};

export default MainRoutes;
